import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Smartphone,
  Key,
  AlertTriangle,
  Download,
  Copy,
  Eye,
  EyeOff,
  CheckCircle,
  Trash2,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useUser } from '@clerk/clerk-react';
import QRCode from 'qrcode';
import { toast } from 'sonner';

interface SecuritySettingsProps {
  userEmail: string;
  userName?: string;
  platform?: 'petso' | 'petso-pro' | 'farmso';
}

export function SecuritySettings({
  userEmail,
  userName,
  platform = 'petso',
}: SecuritySettingsProps) {
  const { t } = useTranslation();
  const { colors, theme } = useTheme();
  const { user } = useUser();
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [disabling, setDisabling] = useState(false);

  const mfaEnabled = user?.totpEnabled ?? false;

  const handleEnable2FAComplete = (backupCodes: string[]) => {
    setRecoveryCodes(backupCodes);
    setShow2FAModal(false);
    toast.success(t('settings.security.twoFAEnabledSuccess'));
  };

  const handleDisable2FA = async () => {
    if (!user) return;
    setDisabling(true);
    try {
      await user.disableTOTP();
      setRecoveryCodes([]);
      toast.success(
        t('settings.security.twoFADisabledSuccess', {
          defaultValue: '2FA has been disabled.',
        })
      );
    } catch (err) {
      console.error('Error disabling 2FA:', err);
      toast.error(
        t('settings.security.twoFADisableError', {
          defaultValue: 'Failed to disable 2FA. Please try again.',
        })
      );
    } finally {
      setDisabling(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3
          className="text-lg font-bold mb-2"
          style={{ color: colors.heading, fontFamily: 'Sora, sans-serif' }}
        >
          {t('settings.security.title')}
        </h3>
        <p
          className="text-sm"
          style={{
            color: colors.textSecondary,
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {t('settings.security.manageAuthDesc')}
        </p>
      </div>

      {/* 2FA Section */}
      <div
        className="p-6 rounded-xl border"
        style={{
          backgroundColor: colors.surfaceHover,
          borderColor: colors.border,
        }}
      >
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <Smartphone
                className="w-6 h-6"
                style={{ color: colors.primary }}
              />
              <h4
                className="font-bold"
                style={{
                  color: colors.heading,
                  fontFamily: 'Sora, sans-serif',
                }}
              >
                {t('settings.security.twoFactor')}
              </h4>
              <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                Soon
              </span>
            </div>
            <p
              className="text-sm mb-4"
              style={{
                color: colors.textSecondary,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {t('settings.security.twoFactorDesc')}
            </p>
            <div className="flex items-center gap-2 mb-4">
              <span
                className="text-sm"
                style={{
                  color: colors.textSecondary,
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                {t('settings.security.status')}
              </span>
              {mfaEnabled ? (
                <span className="px-3 py-1 rounded-lg text-sm font-semibold bg-green-600 text-white">
                  {t('settings.security.enabled')}
                </span>
              ) : (
                <span className="px-3 py-1 rounded-lg text-sm font-semibold bg-orange-600 text-white">
                  {t('settings.security.disabled')}
                </span>
              )}
            </div>
            <div className="flex gap-3">
              {mfaEnabled ? (
                <>
                  <button
                    disabled
                    className="px-4 py-2 rounded-lg opacity-40 cursor-not-allowed transition-opacity"
                    style={{
                      background:
                        'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                      color: '#FFFFFF',
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: 600,
                    }}
                  >
                    {t('settings.security.reconfigure2FA')}
                  </button>
                  {recoveryCodes.length > 0 && (
                    <button
                      disabled
                      className="px-4 py-2 rounded-lg border opacity-40 cursor-not-allowed transition-opacity"
                      style={{
                        backgroundColor: colors.surface,
                        borderColor: colors.border,
                        color: colors.text,
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {t('settings.security.viewRecoveryCodes')}
                    </button>
                  )}
                  <button
                    disabled
                    className="px-4 py-2 rounded-lg border opacity-40 cursor-not-allowed transition-opacity"
                    style={{
                      backgroundColor: colors.surface,
                      borderColor: colors.border,
                      color: colors.text,
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: 600,
                    }}
                  >
                    {t('settings.security.disable2FA', {
                      defaultValue: 'Disable',
                    })}
                  </button>
                </>
              ) : (
                <button
                  disabled
                  className="px-4 py-2 rounded-lg opacity-40 cursor-not-allowed pointer-events-none transition-opacity"
                  style={{
                    background:
                      'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                    color: '#FFFFFF',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 600,
                  }}
                >
                  {t('settings.security.enable2FAButton')}
                </button>
              )}
            </div>
          </div>
          <div
            className="w-20 h-20 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor:
                theme === 'dark' ? 'rgba(99, 102, 241, 0.2)' : '#EEF2FF',
            }}
          >
            <Smartphone className="w-10 h-10 text-indigo-600" />
          </div>
        </div>
      </div>

      {/* Password Reset Section */}
      <div
        className="p-6 rounded-xl border"
        style={{
          backgroundColor: colors.surfaceHover,
          borderColor: colors.border,
        }}
      >
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Key className="w-6 h-6" style={{ color: colors.primary }} />
              <h4
                className="font-bold"
                style={{
                  color: colors.heading,
                  fontFamily: 'Sora, sans-serif',
                }}
              >
                {t('settings.security.password')}
              </h4>
            </div>
            <p
              className="text-sm mb-4"
              style={{
                color: colors.textSecondary,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {t('settings.security.passwordDesc')}
            </p>
            <p
              className="text-sm mb-4"
              style={{
                color: colors.textSecondary,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {t('settings.security.lastChanged')}{' '}
              <span className="font-semibold" style={{ color: colors.text }}>
                30 days ago
              </span>
            </p>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
              style={{
                background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                color: '#FFFFFF',
                fontFamily: 'Sora, sans-serif',
                fontWeight: 600,
              }}
            >
              {t('settings.security.resetPassword')}
            </button>
          </div>
          <div
            className="w-20 h-20 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor:
                theme === 'dark' ? 'rgba(99, 102, 241, 0.2)' : '#EEF2FF',
            }}
          >
            <Key className="w-10 h-10 text-indigo-600" />
          </div>
        </div>
      </div>

      {/* Delete Account Section */}
      <div
        className="p-6 rounded-xl border border-red-200 dark:border-red-900/30"
        style={{
          backgroundColor:
            theme === 'dark' ? 'rgba(239, 68, 68, 0.1)' : '#FEE2E2',
        }}
      >
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
              <h4
                className="font-bold text-red-700 dark:text-red-300"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                {t('settings.security.deleteAccount')}
              </h4>
            </div>
            <p
              className="text-sm mb-4 text-red-600 dark:text-red-400"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('settings.security.deleteAccountDesc')}
            </p>
            <button
              onClick={() => {
                if (confirm(t('settings.security.deleteAccountConfirm'))) {
                  toast.error(t('settings.security.accountDeletionInitiated'));
                }
              }}
              className="px-4 py-2 rounded-lg hover:opacity-90 transition-opacity bg-red-600 text-white"
              style={{
                fontFamily: 'Sora, sans-serif',
                fontWeight: 600,
              }}
            >
              <Trash2 className="w-4 h-4 inline mr-2" />
              {t('settings.security.deleteAccount')}
            </button>
          </div>
          <div
            className="w-20 h-20 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor:
                theme === 'dark' ? 'rgba(239, 68, 68, 0.2)' : '#FEE2E2',
            }}
          >
            <Trash2 className="w-10 h-10 text-red-600 dark:text-red-400" />
          </div>
        </div>
      </div>

      {/* Security Alert */}
      <div
        className="p-4 rounded-xl border"
        style={{
          backgroundColor:
            theme === 'dark' ? 'rgba(245, 158, 11, 0.2)' : '#FEF3C7',
          borderColor: theme === 'dark' ? 'rgba(245, 158, 11, 0.3)' : '#F59E0B',
        }}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 text-orange-600" />
          <div>
            <p
              className="text-sm font-semibold mb-1"
              style={{
                color: theme === 'dark' ? colors.text : '#92400E',
                fontFamily: 'Sora, sans-serif',
              }}
            >
              {t('settings.security.securityBestPractices')}
            </p>
            <ul
              className="text-sm space-y-1"
              style={{
                color: theme === 'dark' ? colors.textSecondary : '#92400E',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              <li>• {t('settings.security.tip1')}</li>
              <li>• {t('settings.security.tip2')}</li>
              <li>• {t('settings.security.tip3')}</li>
              <li>• {t('settings.security.tip4')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 2FA Setup Modal */}
      {show2FAModal && (
        <TwoFactorModal
          onClose={() => setShow2FAModal(false)}
          onComplete={handleEnable2FAComplete}
        />
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <PasswordResetModal
          onClose={() => setShowPasswordModal(false)}
          userEmail={userEmail}
        />
      )}

      {/* Recovery Codes Modal */}
      {showRecoveryModal && (
        <RecoveryCodesModal
          onClose={() => setShowRecoveryModal(false)}
          recoveryCodes={recoveryCodes}
        />
      )}
    </div>
  );
}

// 2FA Setup Modal Component
function TwoFactorModal({
  onClose,
  onComplete,
}: {
  onClose: () => void;
  onComplete: (backupCodes: string[]) => void;
}) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { user } = useUser();
  const [step, setStep] = useState(1);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [loadingTotp, setLoadingTotp] = useState(true);

  // Create TOTP and generate QR code when modal opens
  useEffect(() => {
    if (!user) return;
    const setup = async () => {
      try {
        const totp = await user.createTOTP();
        setSecret(totp.secret);
        const qrDataUrl = await QRCode.toDataURL(totp.uri, {
          width: 160,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        });
        setQrCodeUrl(qrDataUrl);
      } catch (err) {
        console.error('Error creating TOTP:', err);
        toast.error(
          t('settings.security.modal.totpSetupError', {
            defaultValue: 'Failed to initialize 2FA setup. Please try again.',
          })
        );
      } finally {
        setLoadingTotp(false);
      }
    };
    setup();
  }, [user]);

  const handleVerify = async () => {
    if (!user || !verificationCode) return;
    setVerifying(true);
    try {
      const result = await user.verifyTOTP({ code: verificationCode });
      const codes = result.backupCodes ?? [];
      setBackupCodes(codes);
      setStep(3);
    } catch (err) {
      console.error('Error verifying TOTP:', err);
      toast.error(
        t('settings.security.modal.invalidVerificationCode', {
          defaultValue: 'Invalid verification code. Please try again.',
        })
      );
    } finally {
      setVerifying(false);
    }
  };

  const handleComplete = () => {
    onComplete(backupCodes);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
    >
      <div
        className="rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: colors.surface }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b" style={{ borderColor: colors.border }}>
          <h3
            className="text-xl font-bold"
            style={{ color: colors.heading, fontFamily: 'Sora, sans-serif' }}
          >
            {t('settings.security.modal.setup2faTitle', {
              defaultValue: 'Set Up Two-Factor Authentication',
            })}
          </h3>
        </div>

        <div className="p-6 space-y-6">
          {step === 1 && (
            <>
              <div className="text-center">
                <p
                  className="text-sm mb-4"
                  style={{
                    color: colors.textSecondary,
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {t('settings.security.modal.scanQrDescription', {
                    defaultValue:
                      'Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)',
                  })}
                </p>
                <div
                  className="w-48 h-48 mx-auto mb-4 rounded-xl flex items-center justify-center p-4"
                  style={{ backgroundColor: colors.surfaceHover }}
                >
                  {loadingTotp ? (
                    <div
                      className="w-40 h-40 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: '#F3F4F6' }}
                    >
                      <p className="text-xs text-gray-500">
                        {t('settings.security.modal.loadingQrCode', {
                          defaultValue: 'Loading QR Code...',
                        })}
                      </p>
                    </div>
                  ) : qrCodeUrl ? (
                    <img
                      src={qrCodeUrl}
                      alt="2FA QR Code"
                      className="w-full h-full object-contain rounded-lg"
                    />
                  ) : null}
                </div>
                <p
                  className="text-sm mb-4"
                  style={{
                    color: colors.textSecondary,
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {t('settings.security.modal.manualKeyPrompt', {
                    defaultValue: 'Or enter this key manually:',
                  })}
                </p>
                <div
                  className="p-3 rounded-lg border flex items-center justify-between"
                  style={{
                    backgroundColor: colors.surfaceHover,
                    borderColor: colors.border,
                  }}
                >
                  <code
                    className="text-sm font-mono"
                    style={{ color: colors.text }}
                  >
                    {loadingTotp ? '...' : secret}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(secret);
                      toast.success(
                        t('settings.security.modal.secretKeyCopied', {
                          defaultValue: 'Secret key copied to clipboard!',
                        })
                      );
                    }}
                    disabled={loadingTotp}
                    className="p-2 rounded-lg hover:opacity-80 disabled:opacity-50"
                    style={{ backgroundColor: colors.surface }}
                  >
                    <Copy className="w-4 h-4" style={{ color: colors.text }} />
                  </button>
                </div>
              </div>
              <button
                onClick={() => setStep(2)}
                disabled={loadingTotp}
                className="w-full px-6 py-3 rounded-xl text-white hover:opacity-90 disabled:opacity-50"
                style={{
                  background:
                    'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 600,
                }}
              >
                {t('common.continue', { defaultValue: 'Continue' })}
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <div>
                <label
                  className="block text-sm font-semibold mb-2"
                  style={{
                    color: colors.text,
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {t('settings.security.modal.enterVerificationCode', {
                    defaultValue: 'Enter verification code from your app',
                  })}
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="000000"
                  className="w-full px-4 py-3 rounded-lg border text-center text-2xl font-mono"
                  style={{
                    backgroundColor: colors.surfaceHover,
                    borderColor: colors.border,
                    color: colors.text,
                  }}
                  maxLength={6}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleVerify();
                  }}
                />
              </div>
              <button
                onClick={handleVerify}
                disabled={verifying || verificationCode.length < 6}
                className="w-full px-6 py-3 rounded-xl text-white hover:opacity-90 disabled:opacity-50"
                style={{
                  background:
                    'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 600,
                }}
              >
                {verifying
                  ? t('common.loading', { defaultValue: 'Loading...' })
                  : t('settings.security.modal.verify', {
                      defaultValue: 'Verify',
                    })}
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <div className="text-center">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
                <h4
                  className="text-xl font-bold mb-2"
                  style={{
                    color: colors.heading,
                    fontFamily: 'Sora, sans-serif',
                  }}
                >
                  {t('settings.security.modal.twoFaEnabledTitle', {
                    defaultValue: '2FA Enabled Successfully!',
                  })}
                </h4>
                <p
                  className="text-sm mb-6"
                  style={{
                    color: colors.textSecondary,
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {t('settings.security.modal.saveRecoveryCodesDescription', {
                    defaultValue:
                      "Save these recovery codes in a safe place. You'll need them if you lose access to your authenticator.",
                  })}
                </p>
              </div>

              {backupCodes.length > 0 && (
                <div
                  className="p-4 rounded-lg border"
                  style={{
                    backgroundColor: colors.surfaceHover,
                    borderColor: colors.border,
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h5
                      className="font-semibold"
                      style={{
                        color: colors.heading,
                        fontFamily: 'Sora, sans-serif',
                      }}
                    >
                      {t('settings.security.recoveryCodes', {
                        defaultValue: 'Recovery Codes',
                      })}
                    </h5>
                    <button
                      onClick={() => {
                        const text = backupCodes.join('\n');
                        navigator.clipboard.writeText(text);
                        toast.success(
                          t('settings.security.modal.recoveryCodesCopied', {
                            defaultValue: 'Recovery codes copied!',
                          })
                        );
                      }}
                      className="text-sm text-indigo-600 hover:underline flex items-center gap-1"
                    >
                      <Copy className="w-4 h-4" />
                      {t('settings.security.modal.copyAll', {
                        defaultValue: 'Copy All',
                      })}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {backupCodes.map((code, index) => (
                      <div
                        key={index}
                        className="p-2 rounded-lg text-center font-mono text-sm"
                        style={{
                          backgroundColor: colors.surface,
                          color: colors.text,
                        }}
                      >
                        {code}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={handleComplete}
                className="w-full px-6 py-3 rounded-xl text-white hover:opacity-90"
                style={{
                  background:
                    'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 600,
                }}
              >
                {t('common.done', { defaultValue: 'Done' })}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Password Reset Modal Component
function PasswordResetModal({
  onClose,
  userEmail,
}: {
  onClose: () => void;
  userEmail: string;
}) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error(
        t('settings.security.modal.passwordsDoNotMatch', {
          defaultValue: 'Passwords do not match!',
        })
      );
      return;
    }
    toast.success(
      t('settings.security.modal.passwordResetSuccess', {
        defaultValue: 'Password reset successfully!',
      })
    );
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
    >
      <div
        className="rounded-2xl shadow-2xl max-w-md w-full"
        style={{ backgroundColor: colors.surface }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b" style={{ borderColor: colors.border }}>
          <h3
            className="text-xl font-bold"
            style={{ color: colors.heading, fontFamily: 'Sora, sans-serif' }}
          >
            {t('settings.security.resetPassword', {
              defaultValue: 'Reset Password',
            })}
          </h3>
          <p
            className="text-sm mt-1"
            style={{
              color: colors.textSecondary,
              fontFamily: 'Inter, sans-serif',
            }}
          >
            {userEmail}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label
              className="block text-sm font-semibold mb-2"
              style={{ color: colors.text, fontFamily: 'Inter, sans-serif' }}
            >
              {t('settings.security.modal.currentPassword', {
                defaultValue: 'Current Password',
              })}
            </label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 rounded-lg border"
                style={{
                  backgroundColor: colors.surfaceHover,
                  borderColor: colors.border,
                  color: colors.text,
                }}
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                {showCurrent ? (
                  <EyeOff
                    className="w-5 h-5"
                    style={{ color: colors.textSecondary }}
                  />
                ) : (
                  <Eye
                    className="w-5 h-5"
                    style={{ color: colors.textSecondary }}
                  />
                )}
              </button>
            </div>
          </div>

          <div>
            <label
              className="block text-sm font-semibold mb-2"
              style={{ color: colors.text, fontFamily: 'Inter, sans-serif' }}
            >
              {t('settings.security.modal.newPassword', {
                defaultValue: 'New Password',
              })}
            </label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 rounded-lg border"
                style={{
                  backgroundColor: colors.surfaceHover,
                  borderColor: colors.border,
                  color: colors.text,
                }}
                required
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                {showNew ? (
                  <EyeOff
                    className="w-5 h-5"
                    style={{ color: colors.textSecondary }}
                  />
                ) : (
                  <Eye
                    className="w-5 h-5"
                    style={{ color: colors.textSecondary }}
                  />
                )}
              </button>
            </div>
          </div>

          <div>
            <label
              className="block text-sm font-semibold mb-2"
              style={{ color: colors.text, fontFamily: 'Inter, sans-serif' }}
            >
              {t('settings.security.modal.confirmNewPassword', {
                defaultValue: 'Confirm New Password',
              })}
            </label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 rounded-lg border"
                style={{
                  backgroundColor: colors.surfaceHover,
                  borderColor: colors.border,
                  color: colors.text,
                }}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                {showConfirm ? (
                  <EyeOff
                    className="w-5 h-5"
                    style={{ color: colors.textSecondary }}
                  />
                ) : (
                  <Eye
                    className="w-5 h-5"
                    style={{ color: colors.textSecondary }}
                  />
                )}
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 rounded-xl border"
              style={{
                backgroundColor: colors.surface,
                borderColor: colors.border,
                color: colors.text,
                fontFamily: 'Sora, sans-serif',
                fontWeight: 600,
              }}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 rounded-xl text-white hover:opacity-90"
              style={{
                background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: 600,
              }}
            >
              {t('settings.security.resetPassword', {
                defaultValue: 'Reset Password',
              })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Recovery Codes Modal Component
function RecoveryCodesModal({
  onClose,
  recoveryCodes,
}: {
  onClose: () => void;
  recoveryCodes: string[];
}) {
  const { t } = useTranslation();
  const { colors, theme } = useTheme();

  const downloadCodes = () => {
    const text = recoveryCodes.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = t('settings.security.modal.recoveryCodesFilename', {
      defaultValue: 'recovery-codes.txt',
    });
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
    >
      <div
        className="rounded-2xl shadow-2xl max-w-md w-full"
        style={{ backgroundColor: colors.surface }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b" style={{ borderColor: colors.border }}>
          <h3
            className="text-xl font-bold"
            style={{ color: colors.heading, fontFamily: 'Sora, sans-serif' }}
          >
            {t('settings.security.recoveryCodes', {
              defaultValue: 'Recovery Codes',
            })}
          </h3>
          <p
            className="text-sm mt-1"
            style={{
              color: colors.textSecondary,
              fontFamily: 'Inter, sans-serif',
            }}
          >
            {t('settings.security.modal.recoveryCodesDescription', {
              defaultValue:
                'Use these codes if you lose access to your authenticator',
            })}
          </p>
        </div>

        <div className="p-6 space-y-4">
          <div
            className="p-4 rounded-lg border"
            style={{
              backgroundColor: colors.surfaceHover,
              borderColor: colors.border,
            }}
          >
            <div className="grid grid-cols-2 gap-2">
              {recoveryCodes.map((code, index) => (
                <div
                  key={index}
                  className="p-2 rounded-lg text-center font-mono text-sm"
                  style={{
                    backgroundColor: colors.surface,
                    color: colors.text,
                  }}
                >
                  {code}
                </div>
              ))}
            </div>
          </div>

          <div
            className="p-3 rounded-lg"
            style={{
              backgroundColor:
                theme === 'dark' ? 'rgba(245, 158, 11, 0.2)' : '#FEF3C7',
            }}
          >
            <p
              className="text-xs"
              style={{
                color: theme === 'dark' ? colors.text : '#92400E',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              <strong>
                {t('settings.security.modal.important', {
                  defaultValue: 'Important:',
                })}
              </strong>{' '}
              {t('settings.security.modal.recoveryCodesImportant', {
                defaultValue:
                  'Each code can only be used once. Store them in a safe place.',
              })}
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {
                const text = recoveryCodes.join('\n');
                navigator.clipboard.writeText(text);
                toast.success(
                  t('settings.security.modal.recoveryCodesCopied', {
                    defaultValue: 'Recovery codes copied!',
                  })
                );
              }}
              className="flex-1 px-4 py-3 rounded-xl border flex items-center justify-center gap-2 hover:opacity-90"
              style={{
                backgroundColor: colors.surfaceHover,
                borderColor: colors.border,
                color: colors.text,
                fontFamily: 'Sora, sans-serif',
                fontWeight: 600,
              }}
            >
              <Copy className="w-4 h-4" />
              {t('common.copy', { defaultValue: 'Copy' })}
            </button>
            <button
              onClick={downloadCodes}
              className="flex-1 px-4 py-3 rounded-xl border flex items-center justify-center gap-2 hover:opacity-90"
              style={{
                backgroundColor: colors.surfaceHover,
                borderColor: colors.border,
                color: colors.text,
                fontFamily: 'Sora, sans-serif',
                fontWeight: 600,
              }}
            >
              <Download className="w-4 h-4" />
              {t('common.download', { defaultValue: 'Download' })}
            </button>
          </div>

          <button
            onClick={onClose}
            className="w-full px-6 py-3 rounded-xl text-white hover:opacity-90"
            style={{
              background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
              fontFamily: 'Sora, sans-serif',
              fontWeight: 600,
            }}
          >
            {t('common.done', { defaultValue: 'Done' })}
          </button>
        </div>
      </div>
    </div>
  );
}
